import isObject from "../struct-events/is-object.js";
import Tracker from "../prop-trackers/tracker.js";
import TrackerGroup from "../prop-trackers/tracker-group.js";
import PropTracker from "../prop-trackers/prop-tracker.js";
import DeepChangeTracker from "./deep-change-tracker.js";
import DictTracker from "../prop-trackers/dict-tracker.js";

class Properties {
    constructor(modifiedKeys) {
        this._modifiedKeys = modifiedKeys;
        this._dictProps = null;
    }

    _setDictProps(dictProps) {
        this._dictProps = dictProps;
    }

    *iterateModified() {
        for (const key of this._modifiedKeys) {
            yield this._dictProps.get(key);
        }
    }

    *iterateAll() {
        yield* this._dictProps.iterateAll();
    }

    get(key) {
        return this._dictProps.get(key);
    }
}

class PropDeepTracker extends Tracker {
    constructor(strEvtCtx, userdata) {
        super();

        this._strEvtCtx = strEvtCtx;

        this._propTracker = new PropTracker();
        this._deepTrackers = {};
        this._deepTrackerKeys = new WeakMap();
        this._deepTrackerGroup = new TrackerGroup();
        this._dictTracker = new DictTracker();

        this._modifiedKeys = new Set();
        const props = new Properties(this._modifiedKeys);
        this._trackData = Object.freeze({ props, userdata });

        const needsUpdate = () => this.protected_needsUpdate();
        this._propTracker.listenNeedsUpdate(needsUpdate);
        this._deepTrackerGroup.listenNeedsUpdate(needsUpdate);
    }

    track(object, events) {
        this._propTracker.track(object, events);
    }

    protected_startUpdate() {
        if (this._propTracker.needsUpdate) {
            this._propTracker.update(({ props }) => {
                for (const { key, newExists, newValue } of props.iterateModified()) {
                    this._modifiedKeys.add(key);

                    if (newExists) {
                        this._dictTracker.setValue(key, newValue);
                    } else {
                        this._dictTracker.deleteValue(key);
                    }

                    let deepTracker = this._deepTrackers[key];

                    if (isObject(newValue)) {
                        if (!deepTracker) {
                            deepTracker = new DeepChangeTracker(this._strEvtCtx);
                            this._deepTrackers[key] = deepTracker;
                            this._deepTrackerKeys.set(deepTracker, key);
                            this._deepTrackerGroup.add(deepTracker);
                        }

                        deepTracker.track(newValue, this._strEvtCtx.getEvents(newValue));
                    } else {
                        if (deepTracker) {
                            deepTracker.track();
                            delete this._deepTrackers[key];
                            this._deepTrackerGroup.remove(deepTracker);
                        }
                    }
                }
            });
        }

        if (this._deepTrackerGroup.needsUpdate) {
            this._deepTrackerGroup.update(({ trackers }) => {
                for (const deepTracker of trackers) {
                    deepTracker.update();
                    const key = this._deepTrackerKeys.get(deepTracker);
                    this._modifiedKeys.add(key);
                }
            });
        }

        const { props } = this._dictTracker.startUpdate();
        this._trackData.props._setDictProps(props);
        return this._trackData;
    }

    protected_finishUpdate() {
        this._dictTracker.finishUpdate();
        this._modifiedKeys.clear();
    }
}

export default PropDeepTracker;
